<template>
  <div class="console-page">
    <div class="console-page__header">
      <div>
        <span class="label">Run</span>
        <h1 class="console-page__title">{{ job.title }}</h1>
        <p class="console-page__placeholder">
          <span class="console-badge console-badge--small" :class="`console-badge--${job.tone}`">{{ job.statusLabel }}</span>
          <span class="job-detail__stat">{{ job.beadsSummary }}</span>
          <span class="job-detail__stat">{{ job.updatedAtLabel }}</span>
        </p>
      </div>

      <div class="console-page__actions">
        <router-link :to="{ path: '/demo/workspaces/' + job.workspaceId }" class="button button--ghost">Workspace</router-link>
      </div>
    </div>

    <div class="console-page__demo-intro">
      <p class="console-page__intro">A run is one request executed against a workspace. You describe the work. 86 Proof builds the plan. You approve. Each step executes against the workspace's standards. A workspace can have many runs. Each one starts with the standards and corrections previous runs left behind.</p>
    </div>

    <section class="console-panel" :class="`console-panel--${job.tone}`">
      <details class="console-module" open>
        <summary class="console-module__summary">Request</summary>
        <div class="job-detail__prose">
          <p>{{ job.description }}</p>
        </div>
      </details>

      <details class="console-module" open>
        <summary class="console-module__summary">
          Plan
          <span class="console-module__count">{{ job.plan.length }} steps</span>
        </summary>
        <div class="job-detail__plan-list">
          <details v-for="entry in job.plan" :key="entry.index" class="job-detail__plan-entry">
            <summary class="job-detail__plan-summary">
              <span class="job-detail__plan-index">#{{ entry.index }}</span>
              <span class="job-detail__plan-title">{{ entry.title }}</span>
              <span v-if="entry.deps" class="job-detail__plan-deps">depends on {{ entry.deps }}</span>
            </summary>
            <div class="job-detail__prose job-detail__prose--nested">
              <p>{{ entry.task }}</p>
            </div>
          </details>
        </div>
      </details>

      <details class="console-module" open>
        <summary class="console-module__summary">
          Execution
          <span class="console-module__count">{{ completedCount }}/{{ job.beads.length }}</span>
        </summary>

        <div class="dag">
          <div v-for="phase in dagPhases" :key="phase.depth" class="dag__phase">
            <div class="dag__phase-row">
              <div class="dag__phase-label"><span>Phase {{ phase.depth }}</span></div>
              <div class="dag__nodes" :style="{ 'grid-template-columns': `repeat(${dagColumns}, 1fr)` }">
                <button
                  v-for="item in phase.items"
                  :key="item.index"
                  type="button"
                  class="dag__node"
                  :class="[`dag__node--${item.tone}`, { 'dag__node--selected': selectedBeadIndex === item.index }]"
                  :style="{ 'grid-column': `${item.colStart} / ${item.colEnd}` }"
                  @click="toggleBead(item.index)"
                >
                  <span class="dag__node-index">#{{ item.index }}</span>
                  <span class="dag__node-label">{{ item.title }}</span>
                  <span v-if="item.usageLabel" class="dag__node-usage">{{ item.usageLabel }}</span>
                  <span class="dag__node-dot" :class="`dag__node-dot--${item.tone}`" />
                </button>
              </div>
            </div>

            <section v-if="selectedBead && phase.items.some((item) => item.index === selectedBead.index)" class="dag__detail">
              <div class="dag__detail-header">
                <div>
                  <div class="dag__detail-title-row">
                    <span class="dag__detail-index">#{{ selectedBead.index }}</span>
                    <span class="console-badge console-badge--small" :class="`console-badge--${selectedBead.tone}`">{{ selectedBead.statusLabel }}</span>
                  </div>
                  <p class="dag__detail-title">{{ selectedBead.title }}</p>
                </div>
                <button type="button" class="button button--ghost job-detail__close-button" @click="selectedBeadIndex = null">Close</button>
              </div>

              <div class="job-detail__prose">
                <p>{{ selectedBead.fullTitle }}</p>
              </div>

              <div class="job-detail__diag-grid">
                <div class="job-detail__diag-item">
                  <span class="job-detail__diag-label">State</span>
                  <span>{{ selectedBead.statusLabel }}</span>
                </div>
                <div v-if="selectedBead.role" class="job-detail__diag-item">
                  <span class="job-detail__diag-label">Role</span>
                  <span>{{ selectedBead.role }}</span>
                </div>
                <div v-if="selectedBead.beadType" class="job-detail__diag-item">
                  <span class="job-detail__diag-label">Type</span>
                  <span>{{ selectedBead.beadType }}</span>
                </div>
                <div v-if="selectedBead.dependsOnLabel" class="job-detail__diag-item">
                  <span class="job-detail__diag-label">Depends on</span>
                  <span>{{ selectedBead.dependsOnLabel }}</span>
                </div>
                <div v-if="selectedBead.updatedAtLabel" class="job-detail__diag-item">
                  <span class="job-detail__diag-label">Updated</span>
                  <span>{{ selectedBead.updatedAtLabel }}</span>
                </div>
                <div v-if="selectedBead.model" class="job-detail__diag-item">
                  <span class="job-detail__diag-label">Model</span>
                  <span>{{ selectedBead.model }}</span>
                </div>
                <div v-if="selectedBead.usageLabel" class="job-detail__diag-item">
                  <span class="job-detail__diag-label">Tokens</span>
                  <span>{{ selectedBead.usageLabel }}</span>
                </div>
                <div v-if="selectedBead.durationLabel" class="job-detail__diag-item">
                  <span class="job-detail__diag-label">Duration</span>
                  <span>{{ selectedBead.durationLabel }}</span>
                </div>
                <div v-if="selectedBead.costLabel" class="job-detail__diag-item">
                  <span class="job-detail__diag-label">Cost</span>
                  <span>{{ selectedBead.costLabel }}</span>
                </div>
                <div v-if="selectedBead.budgetLabel" class="job-detail__diag-item">
                  <span class="job-detail__diag-label">Budget</span>
                  <span>{{ selectedBead.budgetLabel }}</span>
                </div>
              </div>

              <div class="job-detail__subsection">
                <div class="job-detail__subsection-header">
                  <span class="job-detail__diag-label">Output</span>
                </div>
                <div class="job-detail__prose job-detail__prose--boxed">
                  <p>{{ selectedBead.output }}</p>
                </div>
              </div>

              <details class="job-detail__conversation" :open="Boolean(openConversationIndex === selectedBead.index)" @toggle="onConversationToggle(selectedBead.index, $event)">
                <summary class="job-detail__conversation-summary">Transcript</summary>
                <div class="job-detail__conversation-body">
                  <div class="job-detail__conversation-system">
                    <div class="job-detail__diag-label">System Prompt</div>
                    <pre class="job-detail__conversation-pre">{{ selectedBead.transcript.systemPrompt }}</pre>
                  </div>
                  <div
                    v-for="(message, index) in selectedBead.transcript.messages"
                    :key="`${selectedBead.index}-${index}`"
                    class="job-detail__conversation-message"
                    :class="`job-detail__conversation-message--${message.role}`"
                  >
                    <div class="job-detail__conversation-role">{{ message.role }}</div>
                    <pre class="job-detail__conversation-pre">{{ message.content }}</pre>
                  </div>
                </div>
              </details>
            </section>
          </div>
        </div>

        <p v-if="!selectedBead" class="job-detail__status-line">Select a step to inspect output and conversation evidence.</p>
      </details>

      <details class="console-module" open>
        <summary class="console-module__summary">Results</summary>
        <div class="job-detail__prose">
          <p>{{ job.results.summary }}</p>
        </div>
        <div class="job-detail__acceptance">
          <span v-for="item in job.results.acceptance" :key="item.label" class="console-badge console-badge--small" :class="`console-badge--${item.tone}`">{{ item.label }}</span>
        </div>
      </details>

      <details class="console-module">
        <summary class="console-module__summary">Diagnostics</summary>
        <ul class="job-detail__diagnostic-list">
          <li v-for="item in job.results.diagnostics" :key="item">{{ item }}</li>
        </ul>
      </details>

      <details class="console-module">
        <summary class="console-module__summary">Details</summary>
        <dl class="job-detail__meta-list">
          <div><dt>Run ID</dt><dd class="job-detail__mono">{{ job.id }}</dd></div>
          <div><dt>Workspace</dt><dd>{{ job.workspaceTitle }}</dd></div>
          <div><dt>Created</dt><dd>{{ job.createdAtLabel }}</dd></div>
          <div><dt>Updated</dt><dd>{{ job.updatedAtLabel }}</dd></div>
          <div><dt>Runtime</dt><dd>{{ job.runtime }}</dd></div>
        </dl>
      </details>
    </section>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { demoJob, getDemoJob } from '@/config/demo'

const route = useRoute()
const job = computed(() => getDemoJob(route.params.id) || demoJob)
const selectedBeadIndex = ref(null)
const openConversationIndex = ref(null)

const completedCount = computed(() => job.value.beads.filter((bead) => bead.tone === 'ready').length)
const selectedBead = computed(() => job.value.beads.find((bead) => bead.index === selectedBeadIndex.value) || null)

const dagPhases = computed(() => {
  const groups = new Map()
  for (const bead of job.value.beads) {
    const depth = bead.phase ?? 0
    const items = groups.get(depth) ?? []
    items.push(bead)
    groups.set(depth, items)
  }
  return [...groups.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([depth, items]) => ({
      depth,
      items: items.map((bead, index) => ({
        ...bead,
        colStart: index + 1,
        colEnd: index + 2,
      })),
    }))
})

const dagColumns = computed(() => Math.max(...dagPhases.value.map((phase) => phase.items.length), 1))

function toggleBead(index) {
  selectedBeadIndex.value = selectedBeadIndex.value === index ? null : index
  if (selectedBeadIndex.value !== index) {
    openConversationIndex.value = null
  }
}

function onConversationToggle(index, event) {
  openConversationIndex.value = event.target.open ? index : null
}
</script>
